import React, { useState, useEffect, useContext, useRef } from "react";
import {
  markAnnouncementsRead,
  type Announcement,
  clearAllAnnouncements,
} from "../../services/announcementService";
import { IoClose } from "react-icons/io5";
import "./NotificationsMenu.scss";
import { UserContext } from "components/context/UserContext";
import { useHistory } from "react-router-dom";

interface NotificationsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: (refetch?: boolean) => Promise<void>;
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  isOpen,
  onClose,
  refetch,
}) => {
  const { state, dispatch } = useContext(UserContext)!;
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [unReadIds, setUnReadIds] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<"announcements" | "messages">(
    "announcements"
  );
  const { currentUser, announcements = [], messageNotifications = [] } = state;

  const handleClear = async (notificationId: number) => {
    // console.log("Clearing notification with ID:", notificationId);
    try {
      await clearAllAnnouncements([notificationId]);
      await refetch(true);
    } catch (error) {
      setError("Failed to clear announcements");
      console.error("Error clearing announcements:", error);
    }
  };

  const navigateToChat = (chatId: number) => {
    dispatch({
      type: "REMOVE_MESSAGE_NOTIFICATION",
      payload: { chatId: chatId },
    });
    history.push(`/chat/${chatId}`);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (event.target as Element).closest('[data-notifications-toggle="true"]')
      ) {
        return;
      }

      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        dispatch({
          type: "UPDATE_ANNOUNCEMENTS_READ_STATUS",
          payload: {
            userId: currentUser.id,
            announcementIds: unReadIds,
          },
        });
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, unReadIds]);

  const markAnnouncementsAsRead = async () => {
    try {
      const unreadAnnouncements = announcements.filter(
        (notification: Announcement) => !isNotificationRead(notification)
      );

      if (unreadAnnouncements.length > 0) {
        const unreadIds = unreadAnnouncements.map(
          (notification: Announcement) => notification.id
        );
        await markAnnouncementsRead(unreadIds);
        setUnReadIds(unreadIds);
      }
    } catch (err) {
      setError("Failed to mark announcements as read");
      console.error("Error marking announcements as read:", err);
    }
  };

  useEffect(() => {
    if (isOpen && announcements.length > 0) {
      markAnnouncementsAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isNotificationRead = (notification: Announcement): boolean => {
    return (
      Array.isArray(notification.isSeenBy) &&
      notification.isSeenBy.includes(currentUser.id)
    );
  };
  const handleClearAll = async () => {
    try {
      const announcementIds = announcements.map(
        (announcement) => announcement.id
      );
      await clearAllAnnouncements(announcementIds);
      await refetch(true);
    } catch (error) {
      setError("Failed to clear announcements");
      console.error("Error clearing announcements:", error);
    }
  };
  if (!isOpen) return null;

  return (
    <div ref={menuRef} className="notifications-menu">
      <div className="notifications-header">
        <h3>Notifications</h3>
        <div className="notifications-tabs">
          <button
            className={`tab-btn ${
              activeTab === "announcements" ? "active" : ""
            }`}
            onClick={() => setActiveTab("announcements")}
          >
            Announcements
          </button>
          <button
            className={`tab-btn ${activeTab === "messages" ? "active" : ""}`}
            onClick={() => setActiveTab("messages")}
          >
            Messages
          </button>
        </div>
        {activeTab === "announcements" && announcements?.length > 0 && (
          <button className="view-all-btn" onClick={handleClearAll}>
            Clear All
          </button>
        )}
      </div>

      <div className="notifications-list">
        {loading ? (
          <div className="notifications-loading">Loading...</div>
        ) : error ? (
          <div className="notifications-error">{error}</div>
        ) : activeTab === "announcements" ? (
          announcements.length > 0 ? (
            announcements.map((notification, index) => (
              <div
                key={index}
                className={`notification-item ${
                  !isNotificationRead(notification) ? "unread" : ""
                }`}
              >
                <div className="notification-content">
                  {notification.sender && (
                    <div className="notification-sender">
                      {notification.sender.firstName}{" "}
                      {notification.sender.lastName}
                    </div>
                  )}
                  <div className="notification-wrapper">
                    <p
                      dangerouslySetInnerHTML={{ __html: notification.message }}
                    ></p>
                    <button
                      className="clear-notification-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClear(notification.id);
                      }}
                    >
                      <IoClose size={20} />
                    </button>
                  </div>
                  <span className="notification-time">
                    {new Date(notification.time).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-notifications">No new announcements</div>
          )
        ) : messageNotifications.length > 0 ? (
          messageNotifications.map((message) => (
            <div
              key={message.id}
              className="notification-item unread"
              onClick={() => navigateToChat(message?.chatId)}
            >
              <div className="notification-content">
                <div className="notification-sender">{message.senderName}</div>
                <div className="notification-wrapper">
                  <p>{message.message}</p>
                </div>
                <span className="notification-time">
                  {new Date(message.time).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="no-notifications">No new messages</div>
        )}
      </div>
    </div>
  );
};

export default NotificationsMenu;
